import React from 'react';
import { ApolloError } from '@apollo/client/errors';
import { Pagination } from '@alefeducation/ui-elements-library-core';
import styled from 'styled-components';
import RolesHeader from './RolesHeader';
import RoleItem from './RoleItem';
import { RoleColumn } from './types';
import { Role } from '../types';
import Loading from '../../Loading';
import { NoItemsView } from '../../Organisations';
import PageStatus from '../../Pagination/PageStatus';
import { tableOverflow } from '../../../styles/Common';
import useComponentMount from '../../../pages/hooks/useComponentMount';
import emptyRolesImage from '../../../assets/images/emptyRoles.svg';
import { OOPS_SOMETHING_WENT_WRONG_MESSAGE } from '../../../utils/constants';

const StyledPaginationContainer = styled.div`
  margin: ${({ theme: { space } }: any) => `${space[24]}px`};
  height: 60px;
`;

const StyledRolesBody = styled.div`
  height: calc(100vh - 350px);
  overflow-y: auto;
  ${tableOverflow};
`;

interface Props {
  isLoading?: boolean;
  isViewOnlyRole?: boolean;
  error: ApolloError | undefined;
  roles: Role[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  pageStats: string;
  noItemsContent: {
    title: string;
    description: string;
  };
  headerTitles: RoleColumn[];
}

const RNPTable = ({
  isLoading,
  error,
  totalCount,
  totalPages,
  setCurrentPage,
  currentPage,
  isViewOnlyRole,
  roles,
  pageStats,
  noItemsContent,
  headerTitles,
}: Props) => {
  const isComponentMounted = useComponentMount();

  const showPagination = () => {
    return isComponentMounted && !error && totalPages > 0;
  };

  return (
    <React.Fragment>
      <RolesHeader headerTitles={headerTitles} />
      <StyledRolesBody>
        {isLoading && <Loading />}
        {error && <NoItemsView title={OOPS_SOMETHING_WENT_WRONG_MESSAGE} />}

        {!isLoading && !error && (
          <React.Fragment>
            {totalCount === 0 ? (
              <NoItemsView
                {...noItemsContent}
                image={emptyRolesImage}
                size={{ height: '270px', width: '270px' }}
              />
            ) : (
              roles.map((role: Role, i: number) => (
                <RoleItem
                  role={role}
                  key={i}
                  isViewOnlyRole={isViewOnlyRole}
                  backgroundColor={i % 2 === 0 ? '#ffffff' : '#f9fafa'}
                />
              ))
            )}
          </React.Fragment>
        )}
      </StyledRolesBody>

      <StyledPaginationContainer>
        {showPagination() && (
          <React.Fragment>
            <Pagination
              initialPage={currentPage}
              onPageChange={setCurrentPage}
              pages={totalPages}
              data-testid="pagination"
            />
            <PageStatus isHidden={isLoading}>{pageStats}</PageStatus>
          </React.Fragment>
        )}
      </StyledPaginationContainer>
    </React.Fragment>
  );
};

export default RNPTable;
